import { useEffect, useState } from 'react';
import { canInstall, promptInstall, onInstallChange } from '../lib/pwa.js';
import { isAuthenticated } from '../lib/auth.js';

const DISMISSED_KEY = 'netcom_connect_install_dismissed';
const DISMISS_DAYS = 14; // re-show after 2 weeks

/**
 * InstallBanner — surfaces a "Install Netcom Connect" CTA at the bottom of
 * the viewport on mobile after a short delay.
 *
 * - Android / desktop Chromium: uses beforeinstallprompt
 * - iOS Safari: detected via UA; shows the Share → Add to Home Screen
 *   instructions because Apple doesn't expose programmatic install
 *
 * Dismissable. Stays dismissed for 14 days.
 */
export default function InstallBanner() {
  const [show, setShow] = useState(false);
  const [variant, setVariant] = useState('chromium'); // 'chromium' | 'ios'

  useEffect(() => {
    // Gate on auth — showing "Install Netcom Connect" to a first-time
    // visitor on the public Login page is a noisy "we want to be on
    // your phone" pitch before they've even signed in. Wait until
    // they have a session.
    try { if (!isAuthenticated()) return; } catch { return; }

    // Already installed?
    if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return;
    if (window.navigator && window.navigator.standalone) return;

    // Check dismissal window
    try {
      const dismissedAt = parseInt(localStorage.getItem(DISMISSED_KEY) || '0', 10);
      if (dismissedAt && Date.now() - dismissedAt < DISMISS_DAYS * 86400 * 1000) return;
    } catch { /* ignore */ }

    // iOS Safari detection (Safari is the only iOS browser that supports add-to-home-screen)
    const ua = navigator.userAgent || '';
    const isiOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isStandaloneCapable = 'standalone' in navigator;

    if (isiOS && isStandaloneCapable) {
      setVariant('ios');
      // Show after ~3 seconds so it doesn't compete with the page load
      const timer = setTimeout(() => setShow(true), 3000);
      return () => clearTimeout(timer);
    }

    // Chromium path
    const off = onInstallChange((available) => {
      if (available) {
        setVariant('chromium');
        setTimeout(() => setShow(true), 1500);
      } else {
        setShow(false);
      }
    });
    // Check once in case the prompt already fired
    if (canInstall()) {
      setVariant('chromium');
      setTimeout(() => setShow(true), 1500);
    }
    return off;
  }, []);

  function dismiss() {
    setShow(false);
    try { localStorage.setItem(DISMISSED_KEY, String(Date.now())); } catch { /* ignore */ }
  }

  async function install() {
    const result = await promptInstall();
    if (result.outcome === 'accepted' || result.outcome === 'dismissed') {
      setShow(false);
    }
  }

  if (!show) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-40 px-4 pb-4 safe-bottom">
      <div className="mx-auto max-w-md bg-[var(--color-navy-900)] text-white rounded-xl shadow-2xl border border-white/10 overflow-hidden">
        <div className="p-4 flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-[var(--color-brand-500)] flex items-center justify-center flex-shrink-0">
            <img src="/netcom-logo.png" alt="" className="w-7 h-7 object-contain" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-sm">Install Netcom Connect</div>
            {variant === 'chromium' ? (
              <div className="text-xs text-white/70 mt-0.5">Get one-tap access to your service. Works offline. No app store needed.</div>
            ) : (
              <div className="text-xs text-white/70 mt-0.5">Tap <strong className="text-white">Share</strong> in Safari, then <strong className="text-white">Add to Home Screen</strong>.</div>
            )}
          </div>
          <button onClick={dismiss} aria-label="Dismiss" className="p-2.5 -mt-1.5 -mr-1.5 text-white/60 hover:text-white">
            <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2"><path d="m4 4 8 8M12 4l-8 8" strokeLinecap="round" /></svg>
          </button>
        </div>
        {variant === 'chromium' && (
          <div className="px-4 pb-4 flex items-center gap-2">
            <button onClick={dismiss} className="text-xs font-medium text-white/70 hover:text-white px-2 py-1.5">Not now</button>
            <button onClick={install} className="flex-1 bg-[var(--color-brand-500)] hover:bg-[var(--color-brand-400)] text-slate-950 text-sm font-semibold py-2 px-3 rounded-md">
              Install app
            </button>
          </div>
        )}
        {variant === 'ios' && (
          <div className="px-4 pb-4 text-[11px] text-white/50 flex items-center gap-2">
            <svg className="w-4 h-4 text-[var(--color-brand-500)] flex-shrink-0" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path d="M10 13V3m0 0L6 7m4-4l4 4M4 15v2a2 2 0 002 2h8a2 2 0 002-2v-2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>Look for the share icon in Safari's toolbar</span>
          </div>
        )}
      </div>
    </div>
  );
}
